import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocationEdit } from "./useLocationEdit";
import LocationCanvas from "./LocationCanvas";
import { saveRect } from "./IcleanViewApi";
import IcleanViewGeneral from "./IcleanViewGeneral";
import LiveViewPeoplePanel from "./LiveViewPeoplePanel";
import IcleanViewReaders from "./IcleanViewReaders";
import IcleanViewLocationSettings, { LocationVisibilityControls } from "./IcleanViewLocationSettings";
import IcleanViewUsers from "./IcleanViewUsers";
import IcleanViewCleanRoutes from "./IcleanViewCleanRoutes";
import IcleanViewWashing, { CleaningInfoVisibility } from "./IcleanViewWashing";
import LiveTimeline from "./LiveTimeline";
import { computeLocationAreas } from "./locationArea";
import { cleaningStatesAt, devicePositionsAt } from "./liveReplay";
import DeviceSelectorModal from "../components/DeviceSelectorModal";
import QRScanner from "../components/QRScanner";
import ManualDeviceRegistration from "../components/ManualDeviceRegistration";
import MobileCleanerRouteBar from "../components/MobileCleanerRouteBar";
import axios from "axios";
import { PanelAddButton, PanelScopeToggle } from "../components/PanelListHeader";

const parseLocationJson = (value) => {
  if (value && typeof value === "object") return value;
  if (typeof value !== "string") return {};
  try {
    return JSON.parse(value || "{}");
  } catch {
    return {};
  }
};

const mapLocation = (loc) => {
  const pos = parseLocationJson(loc.position);
  const dim = parseLocationJson(loc.dimensions);
  return {
    id: loc.id,
    is_root: loc.is_root ?? false,
    is_system_generated: loc.is_system_generated ?? false,
    x: pos.x ?? 0,
    y: pos.y ?? 0,
    width: dim.width ?? 100,
    height: dim.height ?? 100,
    name: loc.name,
    type: loc.type || "",
    parent_location_id: loc.parent_location_id ?? null,
    floorplan_id: loc.floorplan_id ?? null,
    loctag_count: loc.loctag_count ?? 0,
    pixels_per_meter: loc.pixels_per_meter ?? null,
    area_m2: loc.area_m2 ?? null,
    effective_pixels_per_meter: loc.effective_pixels_per_meter ?? null,
    scale_source_location_id: loc.scale_source_location_id ?? null,
    floorplan_stored_name: dim.floorplan_stored_name ?? null,
    floorplan_offset_x: dim.floorplan_offset_x ?? 0,
    floorplan_offset_y: dim.floorplan_offset_y ?? 0,
    floorplan_width: dim.floorplan_width ?? dim.width ?? 100,
    floorplan_height: dim.floorplan_height ?? dim.height ?? 100,
    floorplan_crop_left: dim.floorplan_crop_left,
    floorplan_crop_top: dim.floorplan_crop_top,
    floorplan_crop_right: dim.floorplan_crop_right,
    floorplan_crop_bottom: dim.floorplan_crop_bottom,
  };
};

const SidebarSection = ({ title, open, onToggle, children, onAdd = null, addLabel = "New", order = 0 }) => (
  <section className="border-b border-slate-800" style={{ order }}>
    <div className="flex items-center hover:bg-slate-800/70">
      <button
        type="button"
        onClick={onToggle}
        className="flex min-w-0 flex-1 items-center justify-between px-4 py-3 text-left text-[0.7rem] font-semibold uppercase tracking-wide text-slate-200"
      >
        <span>{title}</span>
        <span className={`text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}>⌄</span>
      </button>
      {onAdd && (
        <div className="mr-3">
          <PanelAddButton onClick={onAdd} label={addLabel} />
        </div>
      )}
    </div>
    <div className={open ? "block" : "hidden"}>{children}</div>
  </section>
);

const IcleanView = ({
  selectedCompanyId,
  editMode,
  setEditMode,
  readerMode,
  setReaderMode,
  editLoctagMode,
  setEditLoctagMode,
  addMode,
  setAddMode,
  mopMode,
  setMopMode,
  drawMode,
  setDrawMode,
  cleanRouteMode,
  setCleanRouteMode,
  lifeViewMode,
  setLifeViewMode,
  locationSettingsMode,
  setLocationSettingsMode,
  usersMode,
  setUsersMode,
  user,
  mobilMode,
  mobileCleanRouteActive,
  cleanerMobileMode = false,
  mobileSelectedDate,
  mobileRouteIndex = 0,
  onMobileRoutesLoaded,
  // Registration flow props
  registrationDevice,
  setRegistrationDevice,
  isReading,
  setIsReading,
  onSetRegistrationHandlers,
  // Header dropdown-ban kiválasztott root location id (null = "All")
  activeRootLocationId,
  setActiveRootLocationId,
}) => {
  const svgRef = useRef(null);
  const rectsRef = useRef([]);
  const hasInitialViewRef = useRef(false);
  const [rects, setRects] = useState([]);
  const [technicalLocations, setTechnicalLocations] = useState([]);
  const [technicalCleaningStates, setTechnicalCleaningStates] = useState([]);
  const [showCleaningInfo, setShowCleaningInfo] = useState(true);
  const [cleaningGroups, setCleaningGroups] = useState([]);
  const [cleaningSpeed, setCleaningSpeed] = useState(null);
  // Idővonal és visszajátszás: replayTime null = élő nézet.
  const [replayTime, setReplayTime] = useState(null);
  const [replaySpeed, setReplaySpeed] = useState(1);
  const [replayPlaying, setReplayPlaying] = useState(false);
  const [replayWindow, setReplayWindow] = useState({ hours: 24 });
  const [replayHistory, setReplayHistory] = useState(null);
  const [timelineNow, setTimelineNow] = useState(() => Date.now());
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [editingCleaningGroupId, setEditingCleaningGroupId] = useState(null);
  const [hoveredTechnicalLocationId, setHoveredTechnicalLocationId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [locationTypes, setLocationTypes] = useState([]);
  const fileInputRef = useRef(null);
  const [showCropFor, setShowCropFor] = useState(null);
  const [floorplanEditMode, setFloorplanEditMode] = useState(false);
  const [floorplanEditLocation, setFloorplanEditLocation] = useState(null);
  const [floorplanCropMode, setFloorplanCropMode] = useState(false);
  
  // Loctag state
  const loctagsRef = useRef([]);
  const [loctags, setLoctags] = useState([]);
  const [selectedLoctagId, setSelectedLoctagId] = useState(null);
  const [hoveredLoctagId, setHoveredLoctagId] = useState(null);
  
  // Tracked devices (cleaner icon tracking) - Array, nem Set!
  const [trackedDevices, setTrackedDevices] = useState([]);
  const [deviceEvents, setDeviceEvents] = useState({}); // Device események tárolása
  const [cleanerPositions, setCleanerPositions] = useState([]);
  const [presencePeople, setPresencePeople] = useState([]);
  const [presenceSessions, setPresenceSessions] = useState([]);
  const [presenceDevices, setPresenceDevices] = useState([]);
  const [sidebarSections, setSidebarSections] = useState({
    locations: false,
    people: true,
    devices: true,
    feed: true,
    routes: false,
    cleaningInfo: true,
  });
  const [loctagPanelEl, setLoctagPanelEl] = useState(null);
  const [loctagRegistrationOpen, setLoctagRegistrationOpen] = useState(false);
  const [liveFeedPanelEl, setLiveFeedPanelEl] = useState(null);
  const lastTrackedDevicesRef = useRef('');
  
  // Utolsó feldolgozott event_id device-onként (hogy ne dolgozzuk fel újra ugyanazt)
  const lastProcessedEventRef = useRef({});
  
  // Devices menü dinamikus magassága
  const [devicesMenuHeight, setDevicesMenuHeight] = useState(0);
  
  const [locationHoverFields, setLocationHoverFields] = useState({
    show_borders: true,
    name: true,
    type: true,
    id: false,
    parent_location_id: false,
    loctag_count: false,
    pixels_per_meter: false,
    effective_pixels_per_meter: false,
    scale_source_location_id: false,
    x: false,
    y: false,
    width: false,
    height: false,
    floorplan_id: false,
  });
  const [routeStepMap, setRouteStepMap] = useState({});
  const [checkedRoutes, setCheckedRoutes] = useState([]);
  const [otherRouteLocationIds, setOtherRouteLocationIds] = useState([]);
  const [routeCanvasAppendHandler, setRouteCanvasAppendHandler] = useState(null);
  const [liveRoutePlans, setLiveRoutePlans] = useState([]);
  const [routeSelectionEnabled, setRouteSelectionEnabled] = useState(false);
  const [newUserTrigger, setNewUserTrigger] = useState(0);
  // A felhasználók adatainak szerkesztése a People szekción belül, lenyitható sávban (csak admin).
  const [usersEditOpen, setUsersEditOpen] = useState(false);
  const [peopleScope, setPeopleScope] = useState("active");

  const [mobileRouteDate, setMobileRouteDate] = useState(() =>
    new Date().toISOString().split("T")[0]
  );
  const [mobileRouteLoading, setMobileRouteLoading] = useState(false);
  const [mobilePlan, setMobilePlan] = useState(null);
  const [mobileRoute, setMobileRoute] = useState(null);

  const routeDate = cleanerMobileMode ? mobileSelectedDate : mobileRouteDate;
  const routeModeActive = mobileCleanRouteActive || cleanerMobileMode;

  useEffect(() => {
    if (!selectedCompanyId) {
      setPresencePeople([]);
      setPresenceSessions([]);
      setPresenceDevices([]);
      return;
    }
    let active = true;
    const loadPresence = async () => {
      try {
        const [peopleResponse, sessionsResponse, devicesResponse] = await Promise.all([
          fetch("/api/live-view/people", { credentials: "include" }),
          fetch("/api/device-sessions", { credentials: "include" }),
          fetch("/api/v1/devices", { credentials: "include" }),
        ]);
        const [peopleData, sessionsData, devicesData] = await Promise.all([
          peopleResponse.json(), sessionsResponse.json(), devicesResponse.json(),
        ]);
        if (!active) return;
        setPresencePeople(peopleData.people || []);
        setPresenceSessions((sessionsData.sessions || []).filter((session) => !session.logout_at));
        setPresenceDevices(devicesData.devices || []);
      } catch {}
    };
    loadPresence();
    const interval = setInterval(loadPresence, 5000);
    return () => { active = false; clearInterval(interval); };
  }, [selectedCompanyId]);

  const canvasCleanerPositions = useMemo(() => {
    const peopleById = new Map(presencePeople.map((person) => [String(person.user_id), person]));
    const devicesById = new Map(presenceDevices.map((device) => [device.device_id, device]));
    const sessionByDevice = new Map();
    presenceSessions.forEach((session) => {
      if (!sessionByDevice.has(session.device_id)) sessionByDevice.set(session.device_id, session);
    });
    return cleanerPositions.map((position) => {
      const session = sessionByDevice.get(position.deviceId);
      const person = session ? peopleById.get(String(session.user_id)) : null;
      const device = devicesById.get(position.deviceId);
      return {
        ...position,
        deviceName: device?.device_name || position.deviceId,
        userId: person?.user_id || null,
        userName: person?.name || person?.email || session?.user_name || null,
        userPhotoUrl: person?.photo_url || null,
      };
    });
  }, [cleanerPositions, presencePeople, presenceSessions, presenceDevices]);

  // Területek: a root kézzel megadott területéből arányosan.
  const { locationAreaById, technicalAreaById } = useMemo(
    () => computeLocationAreas(rects, technicalLocations),
    [rects, technicalLocations]
  );

  const saveRootArea = async (location, value) => {
    const area = value === "" || value == null ? null : Number(value);
    try {
      const response = await fetch(`/location/${location.id}/area`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ area_m2: area }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) throw new Error(data.detail || "A terület mentése nem sikerült");
      setRects((previous) => previous.map((rect) => (
        rect.id === location.id ? { ...rect, area_m2: data.area_m2 } : rect
      )));
      loadCleaningGroups();
    } catch (err) {
      alert(err.message);
    }
  };

  const replayActive = replayTime !== null;
  const replayWindowStart = replayWindow.start ?? timelineNow - replayWindow.hours * 3600000;

  // Élőben 5 másodpercenként halad az idővonal jobb széle.
  useEffect(() => {
    const interval = setInterval(() => setTimelineNow(Date.now()), 5000);
    return () => clearInterval(interval);
  }, []);

  const loadReplayHistory = useCallback(async (startTime) => {
    try {
      const params = new URLSearchParams({
        from_ts: new Date(startTime).toISOString(),
        to_ts: new Date().toISOString(),
      });
      const response = await fetch(`/api/live-view/history?${params}`, { credentials: "include" });
      const data = await response.json();
      if (response.ok && data.success) setReplayHistory(data);
    } catch (error) {
      console.error("Hiba a visszajátszási adatok lekérése során:", error);
    }
  }, []);

  const goLive = useCallback(() => {
    setReplayTime(null);
    setReplayPlaying(false);
    setTimelineNow(Date.now());
  }, []);

  const seekReplay = (time) => {
    if (!replayActive) loadReplayHistory(replayWindowStart);
    setReplayPlaying(false);
    setReplayTime(time);
  };

  const finishSeek = (time) => {
    if (time >= Date.now() - 1000) {
      goLive();
      return;
    }
    setReplayTime(time);
    setReplayPlaying(true);
  };

  const changeReplayWindow = (nextWindow) => {
    const start = nextWindow.start ?? Date.now() - nextWindow.hours * 3600000;
    setReplayWindow(nextWindow);
    if (replayActive) {
      loadReplayHistory(start);
      setReplayTime((current) => (current === null ? null : Math.max(current, start)));
    }
  };

  // Lejátszás: a megjelenített idő a választott sebességgel halad; a jelen
  // elérésekor visszaáll az élő nézet.
  const replayTimeRef = useRef(null);
  useEffect(() => { replayTimeRef.current = replayTime; }, [replayTime]);

  useEffect(() => {
    if (!replayActive || !replayPlaying) return undefined;
    const step = 500;
    const interval = setInterval(() => {
      const current = replayTimeRef.current;
      if (current === null) return;
      const next = current + step * replaySpeed;
      if (next >= Date.now()) {
        goLive();
        return;
      }
      replayTimeRef.current = next;
      setReplayTime(next);
    }, step);
    return () => clearInterval(interval);
  }, [replayActive, replayPlaying, replaySpeed, goLive]);

  const replayCleaningStates = useMemo(() => (
    replayActive && replayHistory ? cleaningStatesAt(replayHistory.cleanings, replayTime) : []
  ), [replayActive, replayHistory, replayTime]);

  const replayPositions = useMemo(() => (
    replayActive && replayHistory
      ? devicePositionsAt(replayHistory, replayTime, {
          loctags,
          rects,
          people: presencePeople,
          devices: presenceDevices,
        }).map((position) => ({ ...position, instant: !replayPlaying }))
      : []
  ), [replayActive, replayHistory, replayTime, replayPlaying, loctags, rects, presencePeople, presenceDevices]);

  useEffect(() => {
    const visibleDeviceIds = new Set(presenceSessions.map((session) => session.device_id));
    Object.entries(deviceEvents).forEach(([deviceId, events]) => {
      if ((events || []).some((event) => event.event_type === "LOCTAG_READ")) visibleDeviceIds.add(deviceId);
    });
    if (!visibleDeviceIds.size) return;
    setTrackedDevices((previous) => {
      const next = new Set(previous);
      visibleDeviceIds.forEach((deviceId) => next.add(deviceId));
      return next.size === previous.length ? previous : [...next];
    });
  }, [presenceSessions, deviceEvents]);

  const toggleSidebarSection = (section) => {
    setSidebarSections((previous) => ({ ...previous, [section]: !previous[section] }));
  };

  const handleLocationTreeSelect = (locationId) => {
    const byId = new Map(rects.map((location) => [String(location.id), location]));
    let location = byId.get(String(locationId));
    if (!location) return;

    let root = location;
    while (root.parent_location_id && byId.has(String(root.parent_location_id))) {
      root = byId.get(String(root.parent_location_id));
    }
    if (root?.id && String(activeRootLocationId) !== String(root.id)) {
      setActiveRootLocationId && setActiveRootLocationId(root.id);
    }
    setEditMode && setEditMode(true);
    setDrawMode && setDrawMode(false);
    setSelectedId(location.id);
  };

  useEffect(() => {
    setHoveredTechnicalLocationId(null);
  }, [selectedId, sidebarSections.locations]);

  useEffect(() => {
    if (!routeModeActive || !routeDate) {
      return;
    }
    const loadMyRoute = async () => {
      setMobileRouteLoading(true);
      if (onMobileRoutesLoaded) {
        onMobileRoutesLoaded({ items: [], loading: true });
      }
      try {
        const endpoint = cleanerMobileMode
          ? `/api/my-daily-routes?date=${encodeURIComponent(routeDate)}`
          : `/api/my-daily-route?date=${encodeURIComponent(routeDate)}`;
        const res = await fetch(endpoint, { credentials: "include" });
        const data = await res.json();

        if (cleanerMobileMode && data.success) {
          const items = (data.items || []).slice().sort((a, b) => {
            const ta = a.plan?.scheduled_time || "99:99";
            const tb = b.plan?.scheduled_time || "99:99";
            return ta.localeCompare(tb);
          });
          if (onMobileRoutesLoaded) {
            onMobileRoutesLoaded({ items, loading: false });
          }
          const picked = items[mobileRouteIndex] || items[0];
          if (picked?.route) {
            setMobilePlan(picked.plan);
            setMobileRoute(picked.route);
            const stepMap = {};
            (picked.steps || []).forEach((s) => {
              stepMap[s.location_id] = s.step_order;
            });
            setRouteStepMap(stepMap);
            setCheckedRoutes([picked.route]);
          } else {
            setMobilePlan(null);
            setMobileRoute(null);
            setRouteStepMap({});
            setCheckedRoutes([]);
          }
          return;
        }

        if (data.success && data.route) {
          setMobilePlan(data.plan);
          setMobileRoute(data.route);
          const stepMap = {};
          (data.steps || []).forEach((s) => {
            stepMap[s.location_id] = s.step_order;
          });
          setRouteStepMap(stepMap);
          setCheckedRoutes([data.route]);
        } else {
          setMobilePlan(data.plan || null);
          setMobileRoute(null);
          setRouteStepMap({});
          setCheckedRoutes([]);
        }
      } catch (err) {
        console.error("my-daily-route hiba:", err);
        setMobilePlan(null);
        setMobileRoute(null);
        setRouteStepMap({});
        setCheckedRoutes([]);
        if (onMobileRoutesLoaded) {
          onMobileRoutesLoaded({ items: [], loading: false });
        }
      } finally {
        setMobileRouteLoading(false);
      }
    };
    loadMyRoute();
  }, [routeModeActive, routeDate, cleanerMobileMode, mobileRouteIndex, onMobileRoutesLoaded]);

  // Location settings per-company persistence
  useEffect(() => {
    if (!selectedCompanyId) return;
    const key = `locationSettings_company_${selectedCompanyId}`;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        setLocationHoverFields((prev) => ({ ...prev, ...parsed }));
      }
    } catch (err) {
      console.error("location settings load hiba:", err);
    }
  }, [selectedCompanyId]);

  // Cleaning groupok: a szerkesztett group locationjeit a térképen kattintva
  // lehet be- és kivenni, minden változás azonnal mentődik.
  const loadCleaningGroups = useCallback(async () => {
    try {
      const [response, settingsResponse] = await Promise.all([
        fetch("/api/cleaning-groups", { credentials: "include" }),
        fetch("/api/cleaning-settings", { credentials: "include" }),
      ]);
      const data = await response.json();
      const settings = await settingsResponse.json().catch(() => ({}));
      if (data.success) setCleaningGroups(data.groups || []);
      if (settings.success) setCleaningSpeed(settings.cleaning_speed_m2h);
    } catch (err) {
      console.error("cleaning groups load hiba:", err);
    }
  }, []);

  useEffect(() => {
    setEditingCleaningGroupId(null);
    if (selectedCompanyId) loadCleaningGroups();
    else setCleaningGroups([]);
  }, [selectedCompanyId, loadCleaningGroups]);

  const replaceCleaningGroup = (group) => {
    setCleaningGroups((previous) => previous.map((item) => (item.id === group.id ? group : item)));
  };

  const createCleaningGroup = async () => {
    try {
      const response = await fetch("/api/cleaning-groups", { method: "POST", credentials: "include" });
      const data = await response.json();
      if (!data.success) throw new Error(data.detail || "A cleaning group nem jött létre");
      setCleaningGroups((previous) => [...previous, data.group]);
      setEditingCleaningGroupId(data.group.id);
    } catch (err) {
      alert(err.message);
    }
  };

  const renameCleaningGroup = async (group, name) => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === group.name) return;
    replaceCleaningGroup({ ...group, name: trimmed });
    try {
      const response = await fetch(`/api/cleaning-groups/${group.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: trimmed }),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.detail || "Az átnevezés nem sikerült");
      replaceCleaningGroup(data.group);
    } catch (err) {
      replaceCleaningGroup(group);
      alert(err.message);
    }
  };

  const changeCleaningGroupDuration = async (group, minutes) => {
    // minutes === null: vissza az automatikus időre.
    if (minutes !== null) replaceCleaningGroup({ ...group, duration_minutes: minutes, duration_manual: true });
    try {
      const response = await fetch(`/api/cleaning-groups/${group.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ duration_minutes: minutes }),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.detail || "Az időtartam mentése nem sikerült");
      replaceCleaningGroup(data.group);
    } catch (err) {
      replaceCleaningGroup(group);
      alert(err.message);
    }
  };

  const saveCleaningSpeed = async (value) => {
    try {
      const response = await fetch("/api/cleaning-settings", {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cleaning_speed_m2h: value }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) throw new Error(data.detail || "A takarítási sebesség mentése nem sikerült");
      setCleaningSpeed(data.cleaning_speed_m2h);
      loadCleaningGroups();
    } catch (err) {
      alert(err.message);
    }
  };

  const deleteCleaningGroup = async (group) => {
    if (!window.confirm(`Törlöd a(z) „${group.name}” cleaning groupot?`)) return;
    try {
      const response = await fetch(`/api/cleaning-groups/${group.id}`, { method: "DELETE", credentials: "include" });
      if (!response.ok) throw new Error("A cleaning group nem törlődött");
      setCleaningGroups((previous) => previous.filter((item) => item.id !== group.id));
      if (editingCleaningGroupId === group.id) setEditingCleaningGroupId(null);
    } catch (err) {
      alert(err.message);
    }
  };

  const setCleaningGroupLocation = async (group, locationId, include) => {
    const id = Number(locationId);
    // Azonnali visszajelzés a térképen; a szerver válasza felülírja.
    replaceCleaningGroup({
      ...group,
      location_ids: include
        ? [...group.location_ids.filter((item) => item !== id), id]
        : group.location_ids.filter((item) => item !== id),
    });
    try {
      const response = await fetch(`/api/cleaning-groups/${group.id}/locations/${id}`, {
        method: include ? "POST" : "DELETE",
        credentials: "include",
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.detail || "A mentés nem sikerült");
      replaceCleaningGroup(data.group);
    } catch (err) {
      replaceCleaningGroup(group);
      alert(err.message);
    }
  };

  const editingCleaningGroup = cleaningGroups.find((group) => group.id === editingCleaningGroupId) || null;

  const toggleCleaningGroupLocation = (locationId) => {
    if (!editingCleaningGroup) return;
    const included = editingCleaningGroup.location_ids.includes(Number(locationId));
    setCleaningGroupLocation(editingCleaningGroup, locationId, !included);
  };

  useEffect(() => {
    if (!selectedCompanyId) return;
    const key = `locationSettings_company_${selectedCompanyId}`;
    try {
      localStorage.setItem(key, JSON.stringify(locationHoverFields));
    } catch (err) {
      console.error("location settings save hiba:", err);
    }
  }, [selectedCompanyId, locationHoverFields]);
  
  // Live View route plans auto-betöltés
  useEffect(() => {
    if (!lifeViewMode || !selectedCompanyId) { setLiveRoutePlans([]); return; }
    const today = new Date().toISOString().split("T")[0];
    const load = () =>
      fetch(`/api/live-view/route-plans?date=${today}`, { credentials: "include" })
        .then(r => r.json())
        .then(d => { if (d.success) setLiveRoutePlans(d.plans || []); })
        .catch(() => {});
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [lifeViewMode, selectedCompanyId]);

  // Cleaner pozíciók frissítése - ÚJ TAG ÉRKEZÉSKOR
  useEffect(() => {
    const currentStr = [...trackedDevices].sort().join(',');
    
    // Ha változott a tracked devices lista (be/kipálás)
    if (currentStr !== lastTrackedDevicesRef.current) {
      lastTrackedDevicesRef.current = currentStr;
      console.log("🧹 Tracked devices változott:", currentStr);
      
      if (trackedDevices.length === 0) {
        console.log("🧹 Nincs tracked device, pictogram eltűnik");
        setCleanerPositions([]);
        lastProcessedEventRef.current = {};
        return;
      }
      
      // Ha új device-ot pipáltunk be, azonnal feldolgozzuk a már meglévő event-eket
      console.log("🧹 Bepipálva, feldolgozzuk az event-eket azonnal...");
      // NE return-öljünk, hanem folytassuk az event feldolgozást alább!
    }
    
    // CLEANER POZÍCIÓ KEZELÉS - minden tracked device-hoz
    trackedDevices.forEach(deviceId => {
      const events = deviceEvents[deviceId] || [];
      const loctagReads = events.filter(e => e.event_type === 'LOCTAG_READ');
      
      if (loctagReads.length === 0) {
        // NINCS LOCTAG_READ esemény → cleaner ikon a device mellett álljon (devices menüben)
        console.log(`🧹 Device ${deviceId}: nincs LOCTAG_READ, cleaner ikon devices menüben`);
        
        // Default pozíció: devices menü mellett, device-val egy magasságban
        const defaultX = 340; // Devices menü jobb oldala (320px szélesség + 20px távolság)
        const defaultY = window.innerHeight - 150; // Devices menü magasságában
        
        setCleanerPositions(prev => {
          const existing = prev.find(p => p.deviceId === deviceId);
          if (existing) {
            return prev.map(pos => 
              pos.deviceId === deviceId 
                ? { ...pos, x: defaultX, y: defaultY, instant: true, isDefaultPosition: true }
                : pos
            );
          } else {
            // Még nincs benne, hozzáadjuk
            return [...prev, { deviceId, x: defaultX, y: defaultY, instant: true, isDefaultPosition: true }];
          }
        });
        return;
      }
      
      // VAN LOCTAG_READ esemény → cleaner ikon a legutolsó LOCTAG_READ helyén
      const latestEvent = loctagReads[0];
      const eventId = latestEvent.event_id;
      
      // Ellenőrizzük hogy feldolgoztuk-e már ezt az event-et
      const wasProcessed = lastProcessedEventRef.current[deviceId] === eventId;
      const isFirstLoad = !lastProcessedEventRef.current[deviceId]; // Még nem volt feldolgozva semmi
      
      if (wasProcessed) {
        return; // Már feldolgoztuk, skip
      }
      
      console.log(`${isFirstLoad ? '🎯 ELSŐ' : '🆕 ÚJ'} tag: event_id: ${eventId}, loctag_id: ${latestEvent.loctag_id}`);
      // Loctag keresése
      const loctag = loctags.find(t => String(t.id) === String(latestEvent.loctag_id));
      if (!loctag) {
        console.log(`❌ Loctag ${latestEvent.loctag_id} not found`);
        return;
      }
      
      // Location keresése
      const location = rectsRef.current.find(l => String(l.id) === String(loctag.location_id));
      if (!location) {
        console.log(`❌ Location ${loctag.location_id} not found`);
        return;
      }

      // Csak sikeres koordináta-feloldás után tekintjük feldolgozottnak.
      // Így a később betöltődő loctag/location adatokkal az esemény újrapróbálható.
      lastProcessedEventRef.current[deviceId] = eventId;
      
      // Új pozíció számítása
      const relPos = loctag.relative_position || { x: 0, y: 0 };
      const newX = location.x + relPos.x;
      const newY = location.y + relPos.y;
      
      console.log(`${isFirstLoad ? '📍 INSTANT' : '🎬 MOZGÁS'} loctag #${loctag.id}: (${newX}, ${newY})`);
      setCleanerPositions(prev => {
        const existing = prev.find(p => p.deviceId === deviceId);
        if (existing) {
          return prev.map(pos =>
            pos.deviceId === deviceId
              ? { ...pos, x: newX, y: newY, instant: isFirstLoad, isDefaultPosition: false }
              : pos
          );
        }
        return [...prev, {
          deviceId,
          x: newX,
          y: newY,
          instant: isFirstLoad,
          isDefaultPosition: false,
        }];
      });
    });
  }, [trackedDevices, deviceEvents, loctags]);
  
  // Location Tag Registration local state
  const [showDeviceSelector, setShowDeviceSelector] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [showManualRegistration, setShowManualRegistration] = useState(false);
  const [scannedTags, setScannedTags] = useState([]);
  const [deviceListRefreshTrigger, setDeviceListRefreshTrigger] = useState(0);
  const [registrationStep, setRegistrationStep] = useState(null); // null, 'select_location', 'mark_position', 'verify'
  const [tempScannedTag, setTempScannedTag] = useState(null);
  const [tempLocation, setTempLocation] = useState(null);
  const [tempPosition, setTempPosition] = useState(null);
  const pollingIntervalRef = useRef(null);
  
  // Callback ref a handleLoctagClick-hez (később definiáljuk)
  const loctagClickCallbackRef = useRef(null);

  const {
    draftRef,
    handleMouseDown,
    updateRender,
    canvasOffset,
    canvasZoom,
    handleCanvasDrag,
    handleCanvasDragEnd,
    handleCanvasZoom,
    handleTypeChange,
    handleNameChange,
    handleDelete,
    handleEditBg,
    handleAddBg,
    floorplanCropMode: cropModeFromHook,
    toggleCropMode,
    cropSelection,
    confirmCropSelection,
    saveAndExitFloorplanEdit,
    animateToView,
  } = useLocationEdit({
    svgRef,
    rectsRef,
    setRects,
    editMode: cleanerMobileMode ? false : editMode,
    selectedId,
    setSelectedId,
    selectedCompanyId,
    drawMode: cleanerMobileMode ? false : drawMode,
    setDrawMode,
    fileInputRef,
    floorplanEditMode,
    setFloorplanEditMode,
    floorplanEditLocation,
    setFloorplanEditLocation,
    editLoctagMode,
    addMode,
    onLoctagClick: (e) => loctagClickCallbackRef.current && loctagClickCallbackRef.current(e),
  });

  useEffect(() => {
    if (!cleanerMobileMode || !routeStepMap || Object.keys(routeStepMap).length === 0) return;
    const locs = rects.filter((r) => !r.is_root && routeStepMap[r.id] != null);
    if (locs.length === 0) return;
    const minX = Math.min(...locs.map((l) => l.x));
    const minY = Math.min(...locs.map((l) => l.y));
    const maxX = Math.max(...locs.map((l) => l.x + l.width));
    const maxY = Math.max(...locs.map((l) => l.y + l.height));
    const t = setTimeout(() => {
      animateToView(
        { x: minX, y: minY, width: maxX - minX, height: maxY - minY },
        { padding: 0.82, topInset: 8, bottomInset: 16 }
      );
    }, 200);
    return () => clearTimeout(t);
  }, [cleanerMobileMode, routeStepMap, routeDate, mobileRouteIndex, rects, animateToView]);

  const handleRouteStepClick = (locationId) => {
    const loc = rects.find((r) => String(r.id) === String(locationId));
    if (!loc || loc.is_root) return;
    animateToView(
      { x: loc.x, y: loc.y, width: loc.width, height: loc.height },
      { padding: 0.65, topInset: 8, bottomInset: 16 }
    );
  };

  useEffect(() => {
    setFloorplanCropMode(!!cropModeFromHook);
  }, [cropModeFromHook]);

  useEffect(() => {
    if (!editMode && selectedId !== null) {
      setSelectedId(null);
    }
  }, [editMode, selectedId]);

  useEffect(() => {
    const handleRootEscape = (event) => {
      if (
        event.key === "Escape"
        && activeRootLocationId !== null
        && activeRootLocationId !== undefined
        && !floorplanEditMode
      ) {
        setActiveRootLocationId?.(null);
      }
    };

    window.addEventListener("keydown", handleRootEscape);
    return () => window.removeEventListener("keydown", handleRootEscape);
  }, [activeRootLocationId, floorplanEditMode, setActiveRootLocationId]);

  // Header dropdown-ban kiválasztott root location → animált zoom + pan.
  // Akkor is újrazoomol, ha a jobb oldali panel megnyílik vagy bezárul,
  // hogy a bounds a látható (nem takart) területre kerüljön középre.
  const rightPanelOpen =
    readerMode || editLoctagMode || locationSettingsMode || usersMode || cleanRouteMode || lifeViewMode;
  useEffect(() => {
    if (!rects || rects.length === 0) return;
    if (!svgRef.current) return;

    const computeUnion = (items) => {
      if (!items.length) return null;
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      items.forEach((r) => {
        if (typeof r.x !== "number" || typeof r.y !== "number") return;
        const w = r.width || 0;
        const h = r.height || 0;
        if (r.x < minX) minX = r.x;
        if (r.y < minY) minY = r.y;
        if (r.x + w > maxX) maxX = r.x + w;
        if (r.y + h > maxY) maxY = r.y + h;
      });
      if (!isFinite(minX) || !isFinite(minY)) return null;
      return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
    };

    let bounds = null;
    if (activeRootLocationId) {
      const root = rects.find((r) => r.id === activeRootLocationId);
      if (root && typeof root.x === "number") {
        bounds = { x: root.x, y: root.y, width: root.width, height: root.height };
      }
    } else {
      // "All" mód → összes root location közös bounding box-a
      const roots = rects.filter((r) => !r.parent_location_id);
      bounds = computeUnion(roots);
    }

    if (bounds) {
      // A jobb oldali aside w-80 = 320px széles ha nyitva.
      // Az első betöltésnél azonnal ugrik (duration:0) hogy ne villanjon fel a fehér csík.
      const isFirst = !hasInitialViewRef.current;
      hasInitialViewRef.current = true;
      animateToView(bounds, {
        rightInset: rightPanelOpen ? 320 : 0,
        topAlign: !!activeRootLocationId,
        duration: isFirst ? 0 : 450,
      });
    }
    // animateToView szándékosan kihagyva a depsből (új referencia minden render-re)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRootLocationId, rects.length, rightPanelOpen]);

  useEffect(() => {
    fetch("/location-types")
      .then((res) => res.json())
      .then((data) => setLocationTypes(data))
      .catch((err) => {
        console.error("Nem sikerült betölteni a típusokat:", err);
        setLocationTypes([]);
      });
  }, []);

  useEffect(() => {
    const fetchLocations = async () => {
      if (!selectedCompanyId) return;
      try {
        const res = await fetch(`/locations?company_id=${selectedCompanyId}`);
        const data = await res.json();
        if (data.success) {
          const items = data.locations.map(mapLocation);
          setTechnicalLocations(items.filter((item) => item.type === "technical"));
          rectsRef.current = items.filter((item) => item.type !== "technical");
          updateRender();
        }
      } catch (err) {
        console.error("Hiba a location lekérés során:", err);
      }
    };
    fetchLocations();
  }, [selectedCompanyId]);

  // Loctag-ek betöltése
  useEffect(() => {
    const fetchLoctags = async () => {
      try {
        const res = await fetch('/location_tags', { credentials: 'include' });
        const data = await res.json();
        
        if (data.success && data.location_tags) {
          // Parse relative_position if it's a string
          const parsedTags = data.location_tags.map(tag => ({
            ...tag,
            relative_position: typeof tag.relative_position === 'string' 
              ? JSON.parse(tag.relative_position) 
              : tag.relative_position
          }));
          
          loctagsRef.current = parsedTags;
          setLoctags(parsedTags);
        }
      } catch (err) {
        console.error("Hiba a loctag lekérés során:", err);
      }
    };
    fetchLoctags();
  }, [selectedCompanyId]);

  useEffect(() => {
    if (!selectedCompanyId) {
      setTechnicalCleaningStates([]);
      return undefined;
    }

    let active = true;
    const fetchTechnicalCleaningStates = async () => {
      try {
        const response = await fetch(
          `/api/cleaning/meta-transactions/latest?company_id=${encodeURIComponent(selectedCompanyId)}`,
          { credentials: "include" }
        );
        const data = await response.json();
        if (active && response.ok && data.success) {
          setTechnicalCleaningStates(data.states || []);
        }
      } catch (error) {
        console.error("Hiba a technikai takarítási állapot lekérése során:", error);
      }
    };

    fetchTechnicalCleaningStates();
    const interval = setInterval(fetchTechnicalCleaningStates, 5000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [selectedCompanyId]);

  useEffect(() => {
    if (!selectedCompanyId || Object.keys(deviceEvents).length === 0) return;
    fetch(
      `/api/cleaning/meta-transactions/latest?company_id=${encodeURIComponent(selectedCompanyId)}`,
      { credentials: "include" }
    )
      .then((response) => response.json().then((data) => ({ response, data })))
      .then(({ response, data }) => {
        if (response.ok && data.success) setTechnicalCleaningStates(data.states || []);
      })
      .catch((error) => {
        console.error("Hiba a technikai takarítási állapot frissítése során:", error);
      });
  }, [deviceEvents, selectedCompanyId]);

  const handleUpload = async (e, r) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/gif", "image/bmp", "image/tiff"];
    if (!allowedTypes.includes(file.type)) {
      alert("Only PDF or image files can be uploaded.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("uploaded_by", "1");

    try {
      const response = await axios.post("/upload/media", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      });
      if (response.data && response.data.media_id) {
        // Frissítjük a location floorplan_id-jét és stored_name-et
        r.floorplan_id = response.data.media_id;
        r.floorplan_stored_name = response.data.stored_name;
        await saveRect(r, selectedCompanyId);
        
        // Frissítjük a rectsRef-et is
        const rectIndex = rectsRef.current.findIndex(rect => rect.id === r.id);
        if (rectIndex !== -1) {
          rectsRef.current[rectIndex].floorplan_id = response.data.media_id;
          rectsRef.current[rectIndex].floorplan_stored_name = response.data.stored_name;
        }
        
        updateRender();
        
        // Floorplan edit módba kerül
        setFloorplanEditMode(true);
        setFloorplanEditLocation(r);
        setSelectedId(null); // Kiválasztás törlése
      }
    } catch (error) {
      console.error("Hiba történt a feltöltés során:", error);
      alert("An error occurred during upload.");
    }
  };

  const handleDeleteFloorplan = async () => {
    if (!floorplanEditLocation || !floorplanEditLocation.floorplan_id) return;
    
    const confirmDelete = window.confirm("Are you sure you want to delete the floorplan image?");
    if (!confirmDelete) return;

    try {
      // Törlés a backendről
      const response = await axios.delete(
        `/api/media/${floorplanEditLocation.floorplan_id}`,
        { withCredentials: true }
      );
      
      if (response.data && response.data.success) {
        // Frissítjük a location-t: floorplan_id null
        const updatedLocation = { ...floorplanEditLocation };
        updatedLocation.floorplan_id = null;
        updatedLocation.floorplan_offset_x = 0;
        updatedLocation.floorplan_offset_y = 0;
        updatedLocation.floorplan_width = updatedLocation.width;
        updatedLocation.floorplan_height = updatedLocation.height;
        updatedLocation.floorplan_crop_left = undefined;
        updatedLocation.floorplan_crop_top = undefined;
        updatedLocation.floorplan_crop_right = undefined;
        updatedLocation.floorplan_crop_bottom = undefined;

        await saveRect(updatedLocation, selectedCompanyId);

        // Frissítjük a rectsRef-et
        const rectIndex = rectsRef.current.findIndex(r => r.id === updatedLocation.id);
        if (rectIndex !== -1) {
          rectsRef.current[rectIndex] = updatedLocation;
        }

        // Visszaugrik location edit módba
        setFloorplanEditMode(false);
        setFloorplanEditLocation(null);
        setSelectedId(updatedLocation.id);
        updateRender();
      } else {
        alert("The image could not be deleted.");
      }
    } catch (error) {
      console.error("Hiba a floorplan törlése során:", error);
      alert("An error occurred while deleting the floorplan.");
    }
  };

  const handleScaleChange = async (r, value) => {
    if (!r) return;

    // Child location only inherits from parent/root, no local override.
    if (r.parent_location_id) return;

    const parsed = value === "" ? null : Number(value);
    if (parsed !== null && (!Number.isFinite(parsed) || parsed <= 0)) return;

    r.pixels_per_meter = parsed;
    await saveRect(r, selectedCompanyId);

    // Refresh locations so inherited values update immediately on children.
    try {
      const res = await fetch(`/locations?company_id=${selectedCompanyId}`);
      const data = await res.json();
      if (data.success) {
        const items = data.locations.map(mapLocation);
        setTechnicalLocations(items.filter((item) => item.type === "technical"));
        rectsRef.current = items.filter((item) => item.type !== "technical");
        updateRender();
      }
    } catch (err) {
      console.error("Hiba a scale frissítés során:", err);
    }
  };

  // REGISTER gomb handler - RFID tag regisztráció indítása (Step 1: placement mód)
  const handleRegisterTag = () => {
    // Ellenőrizzük hogy van-e beolvasott tag
    if (!scannedTags || scannedTags.length !== 1 || scannedTags[0] === '---' || scannedTags[0] === 'failure more tag') {
      alert('No RFID tag has been scanned!');
      return;
    }
    
    // Elmentjük a beolvasott tag-et
    setTempScannedTag(scannedTags[0]);
    
    // Bekapcsoljuk az addMode-ot (loctag placement click mód)
    setAddMode(true);
    
    // Step: placing (várunk location click-re)
    setRegistrationStep('placing');
  };
  
  // LOCTAG FIX AND SAVE handler - végleges mentés (Step 2: save)
  const handleConfirmLoctag = async () => {
    // Keressük meg az ideiglenes loctagot
    const tempLoctag = loctags.find(t => t.isTemporary);
    
    if (!tempLoctag) {
      alert('No loctag has been placed!');
      return;
    }
    
    // Mentés az adatbázisba
    const { saveLoctag } = await import('./IcleanViewApi');
    const result = await saveLoctag({
      location_id: tempLoctag.location_id,
      rfid_id: tempLoctag.rfid_id,
      status_id: 'registered',
      relative_position: tempLoctag.relative_position,
      device_id: registrationDevice?.device_id || null
    });
    
    if (result.success) {
      // Frissítjük a loctag listát (töröljük az ideiglenes, betöltjük az újat)
      const fetchLoctags = async () => {
        const res = await fetch('/location_tags', { credentials: 'include' });
        const data = await res.json();
        if (data.success && data.location_tags) {
          const parsedTags = data.location_tags.map(tag => ({
            ...tag,
            relative_position: typeof tag.relative_position === 'string' 
              ? JSON.parse(tag.relative_position) 
              : tag.relative_position
          }));
          
          loctagsRef.current = parsedTags;
          setLoctags(parsedTags);
        }
      };
      await fetchLoctags();
      
      // Cleanup
      setRegistrationStep(null);
      setTempScannedTag(null);
      setAddMode(false);
      
      // Display törlése (tag ID eltűnik)
      setScannedTags(['---']);
    } else {
      alert('An error occurred while saving the loctag!');
    }
  };

  // Loctag handler függvények
  const handleLoctagClick = async (e) => {
    if (!editLoctagMode || !svgRef.current) return;
    
    // Koordináta számítás canvas transform-el
    // Support both mouse and touch events
    const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
    const clientY = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : 0);
    
    const pt = svgRef.current.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const svgP = pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
    const x = (svgP.x - canvasOffset.x) / canvasZoom;
    const y = (svgP.y - canvasOffset.y) / canvasZoom;
    
    // Ha addMode aktív, ideiglenes loctag elhelyezése (REGISTER flow)
    if (addMode && registrationStep === 'placing') {
      
      // Keressük meg a legchildabb location-t az adott pozícióban
      const calculateDepth = (locId) => {
        let depth = 0;
        let currentLoc = rectsRef.current.find(r => r.id === locId);
        while (currentLoc && currentLoc.parent_location_id) {
          depth++;
          currentLoc = rectsRef.current.find(r => r.id === currentLoc.parent_location_id);
        }
        return depth;
      };
      
      const containingLocations = rectsRef.current.filter((r) => 
        x >= r.x && x <= r.x + r.width && y >= r.y && y <= r.y + r.height
      );
      
      if (containingLocations.length === 0) {
        alert("Select a location before placing the loctag!");
        return;
      }
      
      // Legchildabb location kiválasztása (legnagyobb depth)
      containingLocations.sort((a, b) => calculateDepth(b.id) - calculateDepth(a.id));
      const targetLocation = containingLocations[0];
      
      // Relatív pozíció számítás - KEREKÍTVE INTEGER-RE (backend int-et vár!)
      const relativePosition = {
        x: Math.round(x - targetLocation.x),
        y: Math.round(y - targetLocation.y)
      };
      
      // PIROS ideiglenes loctag létrehozása (még NINCS adatbázisban!)
      const tempLoctag = {
        id: 'TEMP_REGISTRATION_LOCTAG',
        rfid_id: tempScannedTag,
        location_id: targetLocation.id,
        relative_position: relativePosition,
        status_id: 'pending',
        isTemporary: true // PIROS lesz
      };
      
      // Hozzáadjuk a loctags listához (vizuálisan megjelenik)
      const updatedLoctags = [...loctagsRef.current, tempLoctag];
      loctagsRef.current = updatedLoctags;
      setLoctags(updatedLoctags);
      
      // Display törlése (tag ID ne maradjon ott)
      setScannedTags(['---']);
      
      // Step váltás: confirm (várunk "LOCTAG FIX AND SAVE" gombra)
      setRegistrationStep('confirm');
      
      // addMode kikapcsolás (már nem kell több click)
        setAddMode(false);
    }
  };
  
  // Beállítjuk a callback ref-et
  loctagClickCallbackRef.current = handleLoctagClick;
  
  const handleLoctagDrag = async (tag, newX, newY) => {
    // Csak IDEIGLENES tag (piros) mozgatható!
    if (!tag.isTemporary) return;
    
    const tagIndex = loctagsRef.current.findIndex(t => t.id === tag.id);
    
    if (tagIndex !== -1 && loctagsRef.current[tagIndex].location_id) {
      const currentLocation = rectsRef.current.find(r => r.id === loctagsRef.current[tagIndex].location_id);
      
      if (currentLocation) {
        loctagsRef.current[tagIndex].relative_position = {
          x: Math.round(newX - currentLocation.x),
          y: Math.round(newY - currentLocation.y)
        };
      setLoctags([...loctagsRef.current]);
      }
    }
  };
  
  const handleLoctagDragEnd = async (tag, finalX, finalY) => {
    // Csak IDEIGLENES tag drag-olható, normál tag-eknek nincs dragEnd
    // (ideiglenes drag-nél nincs mentés, várunk LOCTAG FIX AND SAVE-re)
    return;
  };
  
  const handleLoctagDelete = async (tagId) => {
    const confirmed = window.confirm("Are you sure you want to delete this loctag?");
    if (!confirmed) return;
    
    // Melyik location-hez tartozott?
    const deletedTag = loctagsRef.current.find(t => t.id === tagId);
    const locationId = deletedTag?.location_id;
    
    const { deleteLoctag } = await import('./IcleanViewApi');
    const result = await deleteLoctag(tagId);
    
    if (result.success) {
      // Loctag eltávolítása
      loctagsRef.current = loctagsRef.current.filter(t => t.id !== tagId);
      setLoctags([...loctagsRef.current]);
      setSelectedLoctagId(null);
      
      // Location loctag_count frissítése (-1)
      if (locationId) {
        rectsRef.current = rectsRef.current.map(loc => 
          loc.id === locationId 
            ? { ...loc, loctag_count: Math.max((loc.loctag_count || 0) - 1, 0) }
            : loc
        );
        setRects([...rectsRef.current]);
      }
    }
  };

  // ===== LOCATION TAG REGISTRATION FLOW =====
  
  // Cleanup session
  const cleanupRegistrationSession = async () => {
    const deviceId = localStorage.getItem('registration_device_id');
    
    if (deviceId) {
      try {
        // Device visszaállítása normal módba
        await fetch('/api/device/mode/set', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ device_id: deviceId, operation_mode: 'normal' })
        });
      } catch (err) {
        console.error("Error resetting device mode:", err);
      }
    }
    
    // LocalStorage törlése
    localStorage.removeItem('registration_device_id');
    
    // State reset
    setRegistrationDevice(null);
    setIsReading(false);
    setScannedTags([]);
    setRegistrationStep(null);
    setTempScannedTag(null);
    setTempLocation(null);
    setTempPosition(null);
    
    // Polling stop
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };
  
  // Cleanup amikor editLoctagMode kikapcsol vagy mode változik
  useEffect(() => {
    if (!editLoctagMode && registrationDevice) {
      cleanupRegistrationSession();
    }
  }, [editLoctagMode]);
  
  // Device selector megnyitása (+ gomb)
  const handleOpenDeviceSelector = () => {
    setShowDeviceSelector(true);
  };
  
  // Manual device registration megnyitása (+ gomb)
  const handleOpenManualRegistration = () => {
    setShowManualRegistration(true);
  };
  
  // Device registration success handler
  const handleDeviceRegistrationSuccess = () => {
    // Trigger device list refresh in IcleanViewReaders
    setDeviceListRefreshTrigger(prev => prev + 1);
  };
  
  // Device kiválasztva → QR scanner
  const handleDeviceSelected = (device) => {
    setRegistrationDevice(device);
    setShowQRScanner(true);
  };
  
  // Device direct selection (QR skip)
  const handleDeviceSelectedDirect = async (deviceMac, deviceName) => {
    console.log('Device selected directly:', deviceMac, deviceName);
    
    try {
      // Device mode beállítása: registration_location
      const modeResponse = await fetch('/api/device/mode/set', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          device_id: deviceMac, 
          operation_mode: 'registration_location' 
        })
      });
      
      if (!modeResponse.ok) {
        alert("Failed to set device mode!");
        return;
      }
      
      // LocalStorage: device ID mentése
      localStorage.setItem('registration_device_id', deviceMac);
      
      // Update registration device
      setRegistrationDevice({
        device_id: deviceMac,
        device_name: deviceName
      });
      
    } catch (err) {
      console.error("Device selection error:", err);
      alert("Error selecting device!");
    }
  };
  
  // QR scan success → device mode set
  const handleQRScanSuccess = async (decodedText) => {
    setShowQRScanner(false);
    
    // QR tartalom: device MAC address
    const deviceMac = decodedText.trim();
    
    try {
      // Ellenőrzés: tartozik-e a user cégéhez?
      const checkResponse = await fetch(`/api/v1/devices/check/${deviceMac}`, {
        credentials: 'include'
      });
      
      if (!checkResponse.ok) {
        alert("Device not found or not authorized for your company!");
        setRegistrationDevice(null);
        return;
      }
      
      const checkData = await checkResponse.json();
      
      if (!checkData.registered) {
        alert("This device is not registered!");
        setRegistrationDevice(null);
        return;
      }
      
      // Device mode beállítása: registration_location
      const modeResponse = await fetch('/api/device/mode/set', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          device_id: deviceMac, 
          operation_mode: 'registration_location' 
        })
      });
      
      if (!modeResponse.ok) {
        alert("Failed to set device mode!");
        setRegistrationDevice(null);
        return;
      }
      
      // LocalStorage: device ID mentése
      localStorage.setItem('registration_device_id', deviceMac);
      
      // Update registration device adatokkal
      setRegistrationDevice({
        ...registrationDevice,
        device_id: deviceMac,
        device_name: checkData.device_name || deviceMac
      });
      
      // Step: olvasásra vár
      setRegistrationStep('read_tag');
      
    } catch (err) {
      console.error("QR scan error:", err);
      alert("Error processing QR code!");
      setRegistrationDevice(null);
    }
  };
  
  // READ gomb: start polling
  const startReading = () => {
    const deviceId = localStorage.getItem('registration_device_id');
    if (!deviceId) return;
    
    console.log('[READ] startReading called');
    
    setIsReading(true);
    setScannedTags([]);
    
    // READ command aktiválás (Arduino-nak jelzés)
    console.log('[READ] Sending active:true to backend');
    fetch(`/api/device/read-command/set`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ device_id: deviceId, active: true })
    }).then(() => console.log('[READ] Backend confirmed active:true'))
      .catch(console.error);
    
    // Cache törlése először
    fetch(`/api/rfid/reader/clear-scan/${deviceId}`, {
      method: 'DELETE',
      credentials: 'include'
    }).catch(console.error);
    
    // Tag polling: 200ms (gyors frissítés)
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const response = await fetch(`/api/rfid/reader/latest-scan/${deviceId}`, {
          credentials: 'include'
        });
        const data = await response.json();
        
        if (data.success && data.tag_count > 0) {
          setScannedTags(data.rfid_codes);
        } else {
          // Ha nincs tag (cache üres vagy timeout) → töröljük a display-t
          setScannedTags([]);
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, 200);
  };
  
  // READ gomb: stop polling
  const stopReading = () => {
    const deviceId = localStorage.getItem('registration_device_id');
    
    console.log('[READ] stopReading called');
    
    setIsReading(false);
    
    // Display reset - csak PIROS üzeneteket töröljük (hiba, already registered)
    // ZÖLD tag (valid) MARAD bent!
    setScannedTags(prev => {
      if (!prev || prev.length === 0) return ['---'];
      if (prev.length > 1) return ['---']; // Több tag volt → töröljük
      
      const tag = prev[0];
      if (tag === 'failure more tag') return ['---']; // Hiba → töröljük
      if (tag === '---') return ['---']; // Üres → marad üres
      
      // Ellenőrizzük hogy regisztrálva van-e már
      const isAlreadyRegistered = loctagsRef.current.some(loctag => loctag.rfid_id === tag);
      if (isAlreadyRegistered) return ['---']; // Already registered → töröljük
      
      // Valid új tag → MARAD bent!
      return prev;
    });
    
    // READ command deaktiválás (Arduino-nak jelzés)
    if (deviceId) {
      console.log('[READ] Sending active:false to backend');
      fetch(`/api/device/read-command/set`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_id: deviceId, active: false })
      }).then(() => console.log('[READ] Backend confirmed active:false'))
        .catch(console.error);
    }
    
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };
  
  // Tag olvasás OK → Location selection
  const handleTagReadConfirm = () => {
    if (scannedTags.length !== 1) {
      alert("Please scan exactly ONE tag!");
      return;
    }
    
    setTempScannedTag(scannedTags[0]);
    setRegistrationStep('select_location');
    stopReading();
  };
  
  // Beállítjuk a handler függvényeket az App.js számára
  useEffect(() => {
    if (onSetRegistrationHandlers) {
      onSetRegistrationHandlers({
        onOpenDeviceSelector: handleOpenDeviceSelector,
        onOpenManualRegistration: handleOpenManualRegistration,
        onStartReading: startReading,
        onStopReading: stopReading,
        scannedTags: scannedTags
      });
    }
  }, [onSetRegistrationHandlers, scannedTags]);

  return (
    <div className="w-full h-full relative">
      <input
        type="file"
        ref={fileInputRef}
        onChange={(e) => {
          const r = rects.find((x) => x.id === selectedId);
          if (r) handleUpload(e, r);
        }}
        accept=".pdf,image/*"
        className="hidden"
      />

      {/* Device Selector Modal */}
      <DeviceSelectorModal
        isOpen={showDeviceSelector}
        onClose={() => setShowDeviceSelector(false)}
        onDeviceSelected={handleDeviceSelected}
      />

      {/* QR Scanner Modal */}
      <QRScanner
        isOpen={showQRScanner}
        onScanSuccess={handleQRScanSuccess}
        onError={(err) => {
          console.error("QR scan error:", err);
          alert("QR scanning failed!");
        }}
        onClose={() => {
          setShowQRScanner(false);
          setRegistrationDevice(null);
        }}
      />

      {/* Manual Device Registration Modal */}
      <ManualDeviceRegistration
        isOpen={showManualRegistration}
        onClose={() => setShowManualRegistration(false)}
        companyId={selectedCompanyId}
        onSuccess={handleDeviceRegistrationSuccess}
      />

      {/* Registration step messages - NINCS ÜZENET, addMode egyszerűen működik */}

      {!cleanerMobileMode &&
        !mobilMode &&
        !mobileCleanRouteActive &&
        (readerMode || editLoctagMode || locationSettingsMode || usersMode || cleanRouteMode || lifeViewMode) && (
        <aside className="fixed right-0 top-16 bottom-0 w-80 bg-slate-900 border-l border-slate-800 shadow-xl overflow-y-auto z-40">
          <div className="flex flex-col">
            <SidebarSection order={5} title="Locations" open={sidebarSections.locations} onToggle={() => toggleSidebarSection("locations")}>
              <IcleanViewLocationSettings
                embedded
                locationSettingsMode={locationSettingsMode}
                setLocationSettingsMode={setLocationSettingsMode}
                rects={rects}
                selectedId={selectedId}
                setSelectedId={setSelectedId}
                setEditMode={setEditMode}
                drawMode={drawMode}
                setDrawMode={setDrawMode}
                locationTypes={locationTypes}
                onNameChange={handleNameChange}
                onTypeChange={handleTypeChange}
                onFloorplanAction={(location) => {
                  if (!location) return;
                  if (location.floorplan_id) {
                    handleEditBg(location);
                  } else {
                    handleAddBg(location);
                  }
                }}
                onDelete={handleDelete}
                floorplanEditMode={floorplanEditMode}
                floorplanEditLocation={floorplanEditLocation}
                onToggleCrop={toggleCropMode}
                onDeleteFloorplan={handleDeleteFloorplan}
                onSaveAndExit={saveAndExitFloorplanEdit}
                technicalLocations={technicalLocations.filter((location) => (
                  String(location.parent_location_id) === String(selectedId)
                ))}
                onTechnicalLocationHover={setHoveredTechnicalLocationId}
                onSelectLocation={handleLocationTreeSelect}
                locationAreaById={locationAreaById}
                technicalAreaById={technicalAreaById}
                onRootAreaChange={saveRootArea}
              />
              <LocationVisibilityControls
                locationHoverFields={locationHoverFields}
                setLocationHoverFields={setLocationHoverFields}
                editLoctagMode={editLoctagMode}
                setEditLoctagMode={setEditLoctagMode}
                setAddMode={setAddMode}
                onNewLoctag={() => {
                  setEditLoctagMode(true);
                  setAddMode(true);
                  setLoctagRegistrationOpen(true);
                }}
                loctagExtra={
                  <div ref={setLoctagPanelEl} className={loctagRegistrationOpen ? "block border-t border-slate-800 p-2" : "hidden"} />
                }
              />
            </SidebarSection>

            <SidebarSection
              order={2}
              title="People"
              open={sidebarSections.people}
              onToggle={() => toggleSidebarSection("people")}
            >
              <div className="mx-3 mt-3 overflow-hidden rounded-lg border border-slate-700 bg-slate-950/40">
                <div className="grid min-h-10 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 px-3">
                  <span className="text-[0.7rem] font-semibold text-slate-300">People</span>
                  <PanelScopeToggle
                    value={peopleScope}
                    onChange={setPeopleScope}
                    label="People filter"
                  />
                  <div className="flex justify-self-end">
                    {(user?.type === "admin" || user?.is_iclean_admin) && (
                      <PanelAddButton
                        label="New user"
                        onClick={() => {
                          setUsersEditOpen(true);
                          setNewUserTrigger((value) => value + 1);
                        }}
                      />
                    )}
                  </div>
                </div>
                <div className="overflow-hidden border-t border-slate-800">
                  <LiveViewPeoplePanel embedded scope={peopleScope} />
                </div>
              </div>
              {(user?.type === "admin" || user?.is_iclean_admin) && (
                <div className="mx-3 mt-2 overflow-hidden rounded-lg border border-slate-700 bg-slate-950/40">
                  <button
                    type="button"
                    onClick={() => setUsersEditOpen((open) => !open)}
                    aria-expanded={usersEditOpen}
                    className="flex min-h-9 w-full items-center justify-between px-3 text-[0.7rem] font-semibold text-slate-300 hover:bg-slate-800/60"
                  >
                    <span>Edit users</span>
                    <span className={`text-slate-500 transition ${usersEditOpen ? "rotate-180" : ""}`}>▾</span>
                  </button>
                </div>
              )}
              <IcleanViewUsers
                usersMode={usersMode || usersEditOpen}
                currentUser={user}
                embedded
                createTrigger={newUserTrigger}
                rootLocations={rects.filter((rect) => rect.is_root)}
              />
            </SidebarSection>

            <SidebarSection order={3} title="Devices" open={sidebarSections.devices} onToggle={() => toggleSidebarSection("devices")}>
              <IcleanViewReaders
                selectedCompanyId={selectedCompanyId}
                readerMode
                setReaderMode={setReaderMode}
                editLoctagMode={editLoctagMode}
                setEditLoctagMode={setEditLoctagMode}
                addMode={addMode}
                setAddMode={setAddMode}
                trackedDevices={trackedDevices}
                setTrackedDevices={setTrackedDevices}
                loctags={loctags}
                locations={rects}
                deviceEvents={deviceEvents}
                setDeviceEvents={setDeviceEvents}
                onOpenManualRegistration={handleOpenManualRegistration}
                deviceListRefreshTrigger={deviceListRefreshTrigger}
                onHeightChange={setDevicesMenuHeight}
                registrationDevice={registrationDevice}
                isReading={isReading}
                scannedTags={scannedTags}
                registrationStep={registrationStep}
                onStartReading={startReading}
                onStopReading={stopReading}
                onDeviceSelectedDirect={handleDeviceSelectedDirect}
                onRegisterTag={handleRegisterTag}
                onConfirmLoctag={handleConfirmLoctag}
                embedded
                rootLocations={rects.filter((rect) => rect.is_root)}
                loctagTarget={loctagPanelEl}
                feedTarget={liveFeedPanelEl}
              />
            </SidebarSection>

            <SidebarSection order={6} title="Live feed" open={sidebarSections.feed} onToggle={() => toggleSidebarSection("feed")}>
              <div ref={setLiveFeedPanelEl} className="px-4 pb-4" />
            </SidebarSection>

            <SidebarSection order={4} title="Cleaning route" open={sidebarSections.routes} onToggle={() => toggleSidebarSection("routes")}>
              <IcleanViewCleanRoutes
                cleanRouteMode
                setCleanRouteMode={lifeViewMode ? setLifeViewMode : setCleanRouteMode}
                hideCreate={lifeViewMode}
                rects={rects}
                rootPpm={rects.find((r) => r.is_root)?.pixels_per_meter ?? null}
                onRouteStepMapChange={setRouteStepMap}
                onCheckedRoutesChange={setCheckedRoutes}
                onRouteModeStateChange={setRouteSelectionEnabled}
                onOtherRouteLocationsChange={setOtherRouteLocationIds}
                onCanvasLocationSelect={(handler) => {
                  if (typeof handler === "function") {
                    setRouteCanvasAppendHandler(() => handler);
                  } else {
                    setRouteCanvasAppendHandler(null);
                  }
                }}
                embedded
              />
            </SidebarSection>

            <SidebarSection
              order={1}
              title="Cleaning info"
              open={sidebarSections.cleaningInfo}
              onToggle={() => toggleSidebarSection("cleaningInfo")}
            >
              <IcleanViewWashing
                rects={rects}
                groups={cleaningGroups}
                editingGroupId={editingCleaningGroupId}
                onCreate={createCleaningGroup}
                onEdit={setEditingCleaningGroupId}
                onDelete={deleteCleaningGroup}
                onRename={renameCleaningGroup}
                onDurationChange={changeCleaningGroupDuration}
                locationAreaById={locationAreaById}
                onRemoveLocation={(group, locationId) => setCleaningGroupLocation(group, locationId, false)}
              />
              <CleaningInfoVisibility
                show={showCleaningInfo}
                onChange={setShowCleaningInfo}
                speed={cleaningSpeed}
                onSpeedChange={saveCleaningSpeed}
              />
            </SidebarSection>
          </div>

        </aside>
      )}

      {mobileCleanRouteActive && !cleanerMobileMode && (
        <MobileCleanerRouteBar
          selectedDate={mobileRouteDate}
          onDateChange={setMobileRouteDate}
          route={mobileRoute}
          plan={mobilePlan}
          loading={mobileRouteLoading}
        />
      )}

      {lifeViewMode && !cleanerMobileMode && !mobilMode && !mobileCleanRouteActive && (
        // A fül a fejléc része: fixen a fejléc alsó szegélyére (63px) tapad és fölé kerül,
        // így eltakarja a szegélyvonalat és az árnyékot. Vízszintesen a fejléc középső
        // feliratával egy vonalban (bal menü 64px, jobb panel 320px).
        <div
          className="pointer-events-none fixed z-[160] -translate-x-1/2"
          style={{ top: 63, left: "calc(50% - 128px)" }}
        >
          <LiveTimeline
            windowStart={replayWindowStart}
            now={replayActive ? Math.max(timelineNow, replayTime) : timelineNow}
            replayTime={replayTime}
            speed={replaySpeed}
            windowHours={replayWindow.hours || null}
            onSeek={seekReplay}
            onSeekEnd={finishSeek}
            onSpeedChange={(value) => { setReplaySpeed(value); if (replayActive) setReplayPlaying(true); }}
            onWindowChange={changeReplayWindow}
            onLive={() => { goLive(); setTimelineOpen(false); }}
            expanded={timelineOpen || replayActive}
            onExpand={() => setTimelineOpen(true)}
          />
        </div>
      )}

      <div className={mobileCleanRouteActive && !cleanerMobileMode ? "h-full pt-[76px]" : "h-full"}>
      <LocationCanvas
        svgRef={svgRef}
        rects={rects}
        selectedId={selectedId}
        editMode={editMode}
        drawMode={drawMode}
        floorplanEditMode={floorplanEditMode}
        floorplanEditLocation={floorplanEditLocation}
        handleMouseDown={handleMouseDown}
        draftRef={draftRef}
        onToggleCrop={toggleCropMode}
        floorplanCropMode={floorplanCropMode}
        cropSelection={cropSelection}
        onConfirmCrop={confirmCropSelection}
        onSaveAndExit={saveAndExitFloorplanEdit}
        onDeleteFloorplan={handleDeleteFloorplan}
        onCanvasDragStart={handleCanvasDrag}
        onCanvasDrag={handleCanvasDrag}
        onCanvasDragEnd={handleCanvasDragEnd}
        onRootLocationClick={
          activeRootLocationId === null || activeRootLocationId === undefined
            ? setActiveRootLocationId
            : undefined
        }
        rootSelectionOnly={activeRootLocationId === null || activeRootLocationId === undefined}
        canvasOffset={canvasOffset}
        canvasZoom={canvasZoom}
        editLoctagMode={editLoctagMode}
        addMode={addMode}
        loctags={loctags}
        selectedLoctagId={selectedLoctagId}
        hoveredLoctagId={hoveredLoctagId}
        setHoveredLoctagId={setHoveredLoctagId}
        onLoctagClick={handleLoctagClick}
        onLoctagDrag={handleLoctagDrag}
        onLoctagDragEnd={handleLoctagDragEnd}
        onLoctagDelete={handleLoctagDelete}
        cleanerPositions={showCleaningInfo ? (replayActive ? replayPositions : canvasCleanerPositions) : []}
        liveRoutePlans={lifeViewMode ? liveRoutePlans : []}
        locationSettingsMode={locationSettingsMode}
        locationHoverFields={locationHoverFields}
        cleanRouteMode={cleanRouteMode || mobileCleanRouteActive || cleanerMobileMode}
        routeStepMap={routeStepMap}
        checkedRoutes={checkedRoutes}
        otherRouteLocationIds={mobileCleanRouteActive || cleanerMobileMode ? [] : otherRouteLocationIds}
        routeSelectionEnabled={routeSelectionEnabled && !mobileCleanRouteActive && !cleanerMobileMode}
        onRouteLocationClick={routeCanvasAppendHandler}
        cleaningGroupLocationIds={editingCleaningGroup ? editingCleaningGroup.location_ids : []}
        onCleaningGroupLocationClick={editingCleaningGroup ? toggleCleaningGroupLocation : undefined}
        onRouteStepClick={cleanerMobileMode ? handleRouteStepClick : undefined}
        routeStepClickable={cleanerMobileMode}
        technicalLocations={technicalLocations}
        technicalCleaningStates={showCleaningInfo ? (replayActive ? replayCleaningStates : technicalCleaningStates) : []}
        cleaningNow={replayActive ? replayTime : null}
        locationAreaById={locationAreaById}
        highlightedTechnicalLocationId={hoveredTechnicalLocationId}
      />
      </div>
    </div>
  );
};

export default IcleanView;
